/**
 * Global fetch interceptor that surfaces backend permission denials to the UI.
 *
 * When any fetch — whether raw window.fetch or fetchWithAuth — gets a 403 with
 * a body like `{ error: "Insufficient permissions", required: "orders.edit" }`
 * (the shape returned by requirePermission middleware), we dispatch a
 * CustomEvent("chariot:permission-denied") on window. A listener renders a toast.
 *
 * Runs once at module load. Safe to re-import; the wrapper checks a flag.
 */

const INSTALLED = Symbol.for("chariot.permissionDeniedInstalled");

function install() {
  if (typeof window === "undefined") return;
  if (window[INSTALLED]) return;
  window[INSTALLED] = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function interceptedFetch(input, init) {
    const response = await originalFetch(input, init);

    if (response.status === 403) {
      // Only toast on user-initiated mutations. Auto-load GETs that fail
      // permission checks should silently render empty — popping a toast
      // on every page load for a user who can't see a particular widget
      // is noise, not a useful signal. The UI is expected to gate the
      // widget via can() so the user never sees broken state.
      const method = ((init && init.method) || "GET").toUpperCase();
      const isMutation =
        method === "POST" || method === "PUT" || method === "DELETE" || method === "PATCH";

      if (isMutation) {
        // Clone so we can inspect the body without consuming it for the caller.
        try {
          const clone = response.clone();
          const contentType = clone.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const data = await clone.json();
            if (data && (data.error === "Insufficient permissions" || data.required)) {
              const url = typeof input === "string" ? input : input?.url || "";
              window.dispatchEvent(
                new CustomEvent("chariot:permission-denied", {
                  detail: {
                    required: data.required || null,
                    url,
                    method,
                  },
                }),
              );
            }
          }
        } catch {
          // Silent — a failed clone/read shouldn't break the caller's error path
        }
      }
    }

    return response;
  };
}

install();

export {};
